import { Checkbox } from "@material-tailwind/react";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../_store/userStore";
import Api from "../../api/api.main";
import { MTButton } from "../../components/button/MTButton";
import LineLoginIcon from "../../components/icon/lineloginIcon";
import { PopupMain } from "../../components/popup/PopupMain";
import { LOGO_GOOGLE } from "../../utils/useIcons";
import { useProfileSession } from "./use-profile-session";

// แสดงช่องทาง Login เดิมและส่ง Google Profile เข้า shared session owner ก่อนนำทาง
const LoginMain = () => {
  const navigate = useNavigate();
  const setUserData = useUserStore((state) => state.setUserData);
  const { loadProfileSession } = useProfileSession();
  const [openAlert, setOpenAlert] = useState(false);
  const [openAlertPolicy, setOpenAlertPolicy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isChecked, setChecked] = useState(false);

  const handleCheckboxChange = () => {
    setChecked((current) => !current);
  };

  // ยืนยัน Google token, หา/สร้าง Profile และ restore session ด้วย owner กลาง
  const loginByGmail = useGoogleLogin({
    onSuccess: async (response) => {
      setLoading(true);
      try {
        const { data: userInfo } = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${response.access_token}` },
          }
        );
        const { data: dataProfile } = await Api.get("/profiles/profileLogin", {
          params: { filter: { email: userInfo.email } },
        });

        if (dataProfile?.profiles?.length > 0) {
          const { destination } = await loadProfileSession(
            dataProfile.profiles[0]
          );
          navigate(destination);
          if (destination === "/") setOpenAlert(true);
          return;
        }

        await Api.post("profiles", {
          email: userInfo.email,
          login_type: "google",
        });
        navigate(`/firstlogin?email=${encodeURIComponent(userInfo.email)}`);
      } catch (_error) {
        console.error("Google Login failed");
      } finally {
        setLoading(false);
      }
    },
    scope: "profile email",
  });

  const LINE_CLIENT_ID = process.env.REACT_APP_LINE_CLIENT_ID;
  const LINE_REDIRECT_URI = `${window.location.origin}/login`;
  const LINE_STATE = Math.random().toString(36).substring(2);

  // เริ่ม LINE OAuth flow เดิมโดยไม่เปลี่ยน contract ของ provider
  const loginByLine = () => {
    const lineAuthUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${LINE_CLIENT_ID}&redirect_uri=${LINE_REDIRECT_URI}&state=${LINE_STATE}&scope=profile%20openid%20email`;
    window.location.href = lineAuthUrl;
  };

  // แลก LINE code และใช้ Profile behavior เดิม งานนี้ยังไม่ย้าย LINE เข้า shared Storage hydration
  const handleLineCallback = async (code) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("grant_type", "authorization_code");
      params.append("code", code);
      params.append("redirect_uri", LINE_REDIRECT_URI);
      params.append("client_id", LINE_CLIENT_ID);
      params.append(
        "client_secret",
        process.env.REACT_APP_LINE_SECRET_CHANNEL
      );

      const tokenResponse = await fetch(
        "https://api.line.me/oauth2/v2.1/token",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
        }
      );
      const tokenData = await tokenResponse.json();
      if (!tokenData.access_token) throw new Error("LINE token error");

      const profileResponse = await fetch("https://api.line.me/v2/profile", {
        headers: { Authorization: `Bearer ${tokenData.access_token}` },
      });
      const lineProfile = await profileResponse.json();
      let email = null;
      if (tokenData.id_token) {
        const payload = JSON.parse(atob(tokenData.id_token.split(".")[1]));
        email = payload.email;
      }

      const profileEmail = email || `${lineProfile.userId}@line.me`;
      const { data: dataProfile } = await Api.get("/profiles/profileLogin", {
        params: {
          filter: {
            email: profileEmail,
            line_id: lineProfile.userId,
          },
        },
      });

      if (dataProfile?.profiles?.length > 0) {
        setUserData(dataProfile.profiles[0]);
        navigate("/");
        setOpenAlert(true);
      } else {
        await Api.post("profiles", {
          email: profileEmail,
          login_type: "line",
          line_id: lineProfile.userId,
        });
        navigate(`/firstlogin?email=${encodeURIComponent(profileEmail)}`);
      }
    } catch (_error) {
      console.error("LINE Login failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    if (code && window.location.pathname === "/login") {
      handleLineCallback(code);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // รันเฉพาะตอนเปิดหน้าเพื่อรักษา callback behavior เดิมของ LINE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickLoginByGmail = () => {
    if (!isChecked) {
      setOpenAlertPolicy(true);
      return;
    }
    loginByGmail();
  };

  const onClickLoginByLine = () => {
    if (!isChecked) {
      setOpenAlertPolicy(true);
      return;
    }
    loginByLine();
  };

  return (
    <React.Fragment>
      <div className="font-IBMPlexSansThai flex flex-col justify-between relative min-h-screen mx-auto max-w-3xl">
        <div className="relative pt-[105px] px-[32px]">
          <img
            src="/images/circle_login1.svg"
            className="absolute top-0 right-0"
            alt=""
          />
          <div className="text-maincyan font-bold text-5xl leading-relaxed">
            เข้าสู่ระบบ
          </div>
          <div className="flex flex-col items-center text-mainblue text-base mt-[86px] mb-[15px] text-center -mx-4">
            <div>ทำให้วางแผนการเงินเป็นเรื่องง่าย!</div>
            <div className="font-semibold">
              เข้าสู่ระบบเพื่อทำแผนการเงินของคุณให้เป็นจริงวันนี้
            </div>
          </div>
          <div className="w-full flex flex-col items-center gap-[15px]">
            <MTButton
              loading={loading}
              text="เข้าสู่ระบบด้วย Username"
              color="border border-gray-300"
              colortext="text-center text-gray-400 group-hover:text-white"
              hovercolor="hover:bg-maincyan group"
              rounded="rounded-[10px]"
              width="w-full"
              onClick={() => navigate("/login/username")}
              className="max-w-[350px]"
            />
            <MTButton
              loading={loading}
              iconleft={true}
              icon={<LOGO_GOOGLE className="w-7 h-7" />}
              text="เข้าสู่ระบบด้วย Gmail"
              color="border border-gray-300"
              colortext="text-center text-gray-400 group-hover:text-white"
              flexnone="flex-none"
              hovercolor="hover:bg-maincyan group"
              rounded="rounded-[10px]"
              width="w-full"
              onClick={onClickLoginByGmail}
              className="justify-between max-w-[350px] px-[90px]"
            />
            <MTButton
              loading={loading}
              iconleft={true}
              icon={<LineLoginIcon className="w-7 h-7" />}
              text="เข้าสู่ระบบด้วย Line"
              color="border border-gray-300"
              colortext="text-center text-gray-400 group-hover:text-white"
              flexnone="flex-none"
              hovercolor="hover:bg-maincyan group"
              rounded="rounded-[10px]"
              width="w-full"
              onClick={onClickLoginByLine}
              className="justify-between max-w-[350px] px-[90px]"
            />
            <div className="flex justify-center items-center text-base gap-1.5">
              <Checkbox
                type="checkbox"
                color="cyan"
                checked={isChecked}
                onChange={handleCheckboxChange}
              />
              <div className="text-maingray -ml-2">นโยบายความเป็นส่วนตัว</div>
              <button
                type="button"
                className="text-blue400 hover:font-semibold underline cursor-pointer"
              >
                อ่านเพิ่มเติม
              </button>
            </div>
            <div className="flex justify-center items-center text-base gap-1.5">
              <div className="text-maingray -ml-2">V. 1.0.2</div>
            </div>
          </div>
          <PopupMain
            open={openAlert}
            text="Login เรียบร้อย"
            img="/images/homeicon_smile_1.png"
            onClick={() => {
              navigate("/");
              setOpenAlert(false);
            }}
          />
          <PopupMain
            open={openAlertPolicy}
            text={'กรุณา "ยอมรับ" นโยบายความเป็นส่วนตัวก่อน'}
            img="/images/bad_face.png"
            onClick={() => setOpenAlertPolicy(false)}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default LoginMain;
